import { GAME_OPPOSITE_OPTIONS } from 'betgames-contract-js';
import { keyBy } from 'lodash-unified';
import { GameId } from '@bg-shared/enums/GameId';
import { t } from '@bg-shared/business/Translate';
import { partnerSettingsStore } from '@bg-shared/business/PartnerSettings';
import { CasinoValidationService } from '@bg-shared/business/CasinoBetting/service/CasinoValidation.service';
import {
    ICasinoValidationParams,
    ICasinoValidationError,
    ICasinoRecentActionsModel,
} from '@bg-shared/business/CasinoBetting/interfaces';

export class RedBlueValidationService extends CasinoValidationService {
    private oppositeOptionsMap: Record<string, string> = {};

    constructor(private readonly gameId: GameId) {
        super(gameId);

        if (partnerSettingsStore.isOppositeBettingRestricted) {
            this.buildOppositeOptionsMap(gameId);

            this.defaultAddValidators.push(this.validateOppositeBet);
            this.defaultMoveValidators.push(this.validateOppositeBet);
        }
    }

    private buildOppositeOptionsMap(gameId: GameId) {
        if (!this.gameOptionsStore.value) {
            return;
        }

        const oppositeClasses = GAME_OPPOSITE_OPTIONS[gameId] as Record<string, string>;
        const groupedOptions = keyBy(this.gameOptionsStore.value.options, 'optionClass');

        Object.entries(oppositeClasses).forEach(([optionClass, oppositeClass]) => {
            if (groupedOptions[optionClass] && groupedOptions[oppositeClass]) {
                this.oppositeOptionsMap[groupedOptions[optionClass].id] =
                    groupedOptions[oppositeClass].id.toString();
            }
        });
    }

    private validateOppositeBet(params: ICasinoValidationParams): ICasinoValidationError {
        const oppositeOptionId = this.oppositeOptionsMap[params.optionItemID];
        const oppositeHasBet = this.betAmountsStore.value[oppositeOptionId] > 0;

        const isMovingFromOpposite =
            oppositeHasBet &&
            params.moveFromOptionItemID &&
            params.moveFromOptionItemID === oppositeOptionId;

        if (oppositeHasBet && !isMovingFromOpposite) {
            return {
                ...this.defaultValidationError,
                message: t.string('cannot_place_bets_on_both_outcomes'),
            };
        }

        return null;
    }

    private validateOppositeBetsRepeat(bets: ICasinoRecentActionsModel['recentBets']): boolean {
        const betClasses = bets.map((bet) => bet.oddClass);

        const hasOppositeBet = betClasses.some((betClass) => {
            const oppositeBetClass = GAME_OPPOSITE_OPTIONS[this.gameId][betClass] as string;
            return oppositeBetClass && betClasses.includes(oppositeBetClass);
        });

        if (hasOppositeBet) {
            super.showError(t.string('cannot_place_bets_on_both_outcomes'));
            return false;
        }

        return true;
    }

    public validateRepeat(bets: ICasinoRecentActionsModel['recentBets']): boolean {
        const isValid = super.validateRepeat(bets);

        if (!isValid) {
            return false;
        }

        if (partnerSettingsStore.isOppositeBettingRestricted) {
            return this.validateOppositeBetsRepeat(bets);
        }

        return true;
    }
}
